// Sprint 11b.1 — "+ New project" modal (§2.4).
//
// Broadcasts POST /api/projects to every registered server in parallel.
// Shows per-server status inline. Modal closes when all succeed or user
// clicks "Done" with at least one success.

import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useServersStore, type ServerEntry } from '../stores/servers.store';
import { useTranslation } from '../i18n';
import { Icon } from './atoms/Icon';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface NewProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (slug: string) => void;
}

type ServerStatus = 'idle' | 'cloning' | 'ready' | 'error';

interface ServerResult {
  serverId: string;
  serverName: string;
  status: ServerStatus;
  error?: string;
}

const SLUG_REGEX = /^[a-z0-9][a-z0-9._-]*$/;

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '-')
    .replace(/^[^a-z0-9]+/, '')
    .replace(/-+/g, '-')
    .slice(0, 100);
}

export function NewProjectModal({
  open,
  onOpenChange,
  onCreated,
}: NewProjectModalProps) {
  const { t } = useTranslation();
  const servers = useServersStore((s) => s.servers);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [repoUrl, setRepoUrl] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('main');
  const [results, setResults] = useState<ServerResult[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setSlug('');
      setSlugManual(false);
      setRepoUrl('');
      setDefaultBranch('main');
      setResults([]);
      setSubmitting(false);
    }
  }, [open]);

  function handleNameBlur() {
    if (!slugManual && name.trim()) {
      setSlug(toSlug(name));
    }
  }

  const isValid =
    name.trim().length > 0 &&
    slug.length > 0 &&
    SLUG_REGEX.test(slug);

  const effectiveServers: Pick<ServerEntry, 'id' | 'name' | 'baseUrl'>[] =
    servers.length > 0
      ? servers
      : [{ id: 'local', name: 'This server', baseUrl: '' }];

  async function submit() {
    if (!isValid || submitting) return;
    setSubmitting(true);

    const serversSnapshot = effectiveServers;
    const initial: ServerResult[] = serversSnapshot.map((s) => ({
      serverId: s.id,
      serverName: s.name,
      status: 'cloning',
    }));
    setResults(initial);

    const body = {
      id: slug,
      name: name.trim(),
      repoUrl: repoUrl.trim() || undefined,
      defaultBranch: defaultBranch.trim() || 'main',
    };

    const promises = serversSnapshot.map(async (server) => {
      try {
        await api('/api/projects', {
          method: 'POST',
          body,
          baseUrl: server.baseUrl || undefined,
        });
        setResults((prev) =>
          prev.map((r) =>
            r.serverId === server.id ? { ...r, status: 'ready' as const } : r,
          ),
        );
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : 'Unknown error';
        setResults((prev) =>
          prev.map((r) =>
            r.serverId === server.id
              ? { ...r, status: 'error' as const, error: msg }
              : r,
          ),
        );
      }
    });

    await Promise.allSettled(promises);
    setSubmitting(false);
  }

  const anySuccess = results.some((r) => r.status === 'ready');
  const allDone = results.length > 0 && results.every((r) => r.status !== 'cloning');
  const allSuccess = results.length > 0 && results.every((r) => r.status === 'ready');

  function handleDone() {
    if (anySuccess) {
      onCreated(slug);
    }
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-project-modal">
        <DialogHeader>
          <DialogTitle>{t('workspace.newProject')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-name">{t('settings.projects.name')}</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameBlur}
              placeholder={t('workspace.namePlaceholder')}
              maxLength={200}
              disabled={submitting}
              data-testid="new-project-name"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-slug">{t('settings.projects.slug')}</Label>
            <Input
              id="project-slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugManual(true);
              }}
              placeholder={t('workspace.slugPlaceholder')}
              maxLength={100}
              disabled={submitting}
              data-testid="new-project-slug"
            />
            {slug && !SLUG_REGEX.test(slug) && (
              <p className="text-caption text-destructive">
                {t('workspace.slugInvalid')}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-repo">{t('workspace.repoUrl')}</Label>
            <Input
              id="project-repo"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              placeholder={t('workspace.repoPlaceholder')}
              disabled={submitting}
              data-testid="new-project-repo"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-branch">
              {t('settings.projects.defaultBranch')}
            </Label>
            <Input
              id="project-branch"
              value={defaultBranch}
              onChange={(e) => setDefaultBranch(e.target.value)}
              placeholder={t('workspace.branchPlaceholder')}
              maxLength={100}
              disabled={submitting}
              data-testid="new-project-branch"
            />
          </div>

          {results.length > 0 && (
            <div
              className="flex flex-col gap-2 border border-border rounded-md p-3"
              data-testid="new-project-server-results"
            >
              {results.map((r) => (
                <div
                  key={r.serverId}
                  className="flex items-center gap-2 text-body"
                >
                  <span className="flex-1 truncate">{r.serverName}</span>
                  {r.status === 'cloning' && (
                    <span className="text-muted-foreground text-caption">
                      {t('workspace.cloning')}
                    </span>
                  )}
                  {r.status === 'ready' && (
                    <Icon
                      name="Check"
                      size={16}
                      className="text-status-success shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  {r.status === 'error' && (
                    <span
                      className="text-destructive text-caption truncate max-w-[200px]"
                      title={r.error}
                    >
                      {r.error}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          {allDone && anySuccess ? (
            <Button onClick={handleDone} data-testid="new-project-done">
              {allSuccess ? t('workspace.done') : t('workspace.done')}
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={!isValid || submitting}
              data-testid="new-project-submit"
            >
              {submitting ? t('workspace.creating') : t('workspace.create')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
